<template>
  <v-container fluid class="public-activation-container">
    <h1 class="text-center">{{ appName }}</h1>
    <v-row justify="center">
      <v-col cols="12" :lg="6" :xl="6">
        <v-card class="parking-form" elevation="2">
          <v-card-title class="pb-2">
            <h2 class="form-title">Estacionar Veículo</h2>
          </v-card-title>

          <v-card-text>
            <v-form ref="form" v-model="isFormValid">
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                      v-model="vehiclePlate"
                      :error-messages="vehiclePlateErrors"
                      label="Veículo"
                      placeholder="Digite uma placa de veículo válida"
                      outlined
                      dense
                      :rules="vehiclePlateRules"
                      :loading="isCheckingNotifications || isCheckingVehicle"
                      @input="handlePlateInput"
                  />
                </v-col>

                <v-col cols="12" md="4" v-if="groupOptions.length > 1">
                  <v-select
                      v-model="selectedGroup"
                      :items="groupOptions"
                      label="Grupo de produto"
                      outlined
                      dense
                      item-text="text"
                      item-value="value"
                      :rules="[v => !!v || 'Selecione um grupo']"
                  />
                </v-col>

                <v-col cols="12" md="4">
                  <v-select
                      v-model="selectedCategory"
                      :items="categoryOptions"
                      label="Categoria"
                      outlined
                      dense
                      item-text="text"
                      item-value="value"
                  />
                </v-col>

                <v-col cols="12" md="4">
                  <v-select
                      v-model="selectedProduct"
                      :items="filteredTimeOptions"
                      label="Tempo"
                      outlined
                      dense
                      item-text="text"
                      item-value="value"
                  />
                </v-col>
              </v-row>

              <v-expand-transition>
                <v-row v-if="vehicleDetails" class="mb-2">
                  <v-col cols="12">
                    <v-card outlined class="vehicle-info-card">
                      <v-card-text class="pa-3 d-flex align-center">
                        <v-avatar color="primary" size="40" class="mr-3">
                          <i class="fas fa-car text-white"></i>
                        </v-avatar>
                        <div>
                          <div class="subtitle-2 primary--text font-weight-bold">
                            Veículo Identificado
                          </div>
                          <div class="body-2 text--primary">
                            {{ vehicleDisplayName }}
                          </div>
                          <div class="caption grey--text">
                            {{ vehicleColorAndType }}
                          </div>
                        </div>
                        <v-spacer></v-spacer>
                        <v-icon color="success" small>mdi-check-circle</v-icon>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-expand-transition>

              <v-row v-if="hasNotifications">
                <v-col cols="12">
                  <v-alert
                      type="warning"
                      dense
                      outlined
                      class="mb-2"
                  >
                    <div class="notification-alert">
                      <div class="notification-alert-content">
                        <v-icon left small>mdi-alert-circle</v-icon>
                        <span class="notification-title">Atenção: Este veículo possui {{ notifications.length }} notificação(ões) pendente(s)</span>
                      </div>
                      <div class="notification-alert-actions">
                        <v-btn
                            text
                            small
                            color="warning"
                            @click="toggleNotificationDetails"
                            class="mr-2"
                        >
                          {{ showNotificationDetails ? 'Ocultar' : 'Ver detalhes' }}
                        </v-btn>
                        <v-btn
                            small
                            color="error"
                            dark
                            @click="showNotificationPayment"
                            :loading="isProcessingNotificationPayment"
                        >
                          Pagar Notificações
                        </v-btn>
                      </div>
                    </div>
                  </v-alert>

                  <v-expand-transition>
                    <div v-if="showNotificationDetails" class="notification-details">
                      <v-card outlined class="mb-2">
                        <v-card-title class="py-2">
                          <span class="notification-details-title">Detalhes das Notificações</span>
                        </v-card-title>
                        <v-card-text class="pa-3">
                          <div
                              v-for="(notification, index) in notifications"
                              :key="notification.id"
                              class="notification-item"
                              :class="{ 'mb-4': index < notifications.length - 1 }"
                          >
                            <div class="notification-card">
                              <div class="notification-header">
                                <div class="notification-type-section">
                                  <span class="notification-type">{{ getNotificationTypeName(notification) }}</span>
                                  <span class="notification-status">{{ notification.status_enum }}</span>
                                </div>
                                <span class="notification-amount">
                                  R$ {{ formatMoney(notification.price) }}
                                </span>
                              </div>

                              <div class="notification-details-content">
                                <div class="notification-info-row">
                                  <v-icon small class="mr-1">mdi-calendar</v-icon>
                                  <span class="notification-date">{{ formatNotificationDate(notification.date_time) }}</span>
                                </div>

                                <div class="notification-info-row" v-if="notification.address">
                                  <v-icon small class="mr-1">mdi-map-marker</v-icon>
                                  <span class="notification-location">{{ notification.address }}</span>
                                </div>

                                <div class="notification-info-row" v-if="notification.vehicle">
                                  <v-icon small class="mr-1">mdi-car</v-icon>
                                  <span class="notification-vehicle">
                                    {{ getVehicleDescription(notification.vehicle) }}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          <v-divider class="my-3"></v-divider>

                          <div class="notification-total-section">
                            <div class="notification-total">
                              <span class="total-label">Total das notificações:</span>
                              <span class="total-amount">R$ {{ formatMoney(totalNotificationAmount) }}</span>
                            </div>
                            <v-btn
                                color="error"
                                dark
                                block
                                large
                                @click="showNotificationPayment"
                                :loading="isProcessingNotificationPayment"
                                class="mt-3"
                            >
                              <v-icon left>mdi-credit-card</v-icon>
                              Pagar Todas as Notificações
                            </v-btn>
                          </div>
                        </v-card-text>
                      </v-card>
                    </div>
                  </v-expand-transition>
                </v-col>
              </v-row>

              <v-row v-if="showNotificationPaymentSuccess">
                <v-col cols="12">
                  <v-alert
                      type="success"
                      dense
                      outlined
                      class="mb-2"
                  >
                    <div class="notification-success-alert">
                      <v-icon left small>mdi-check-circle</v-icon>
                      <span class="notification-success-title">Pagamento das notificações realizado com sucesso!</span>
                    </div>
                  </v-alert>
                </v-col>
              </v-row>

              <v-dialog v-model="showPaymentDialog" max-width="500" persistent>
                <v-card>
                  <v-card-title class="headline">
                    <v-icon left color="error">mdi-alert-circle</v-icon>
                    Pagamento de Notificações
                  </v-card-title>

                  <v-card-text>
                    <div class="payment-dialog-content">
                      <p class="mb-3">Você está prestes a pagar {{ notifications.length }} notificação(ões) pendente(s).</p>

                      <div class="payment-summary">
                        <div class="summary-row" v-for="notification in notifications" :key="notification.id">
                          <span>{{ getNotificationTypeName(notification) }} - {{ notification.plate }}</span>
                          <span class="summary-amount">R$ {{ formatMoney(notification.price) }}</span>
                        </div>

                        <v-divider class="my-2"></v-divider>

                        <div class="summary-total">
                          <strong>Total: R$ {{ formatMoney(totalNotificationAmount) }}</strong>
                        </div>
                      </div>

                      <div class="payment-method-info mt-4">
                        <v-icon left>mdi-qrcode</v-icon>
                        <span>Método de pagamento: <strong>PIX</strong></span>
                      </div>
                    </div>
                  </v-card-text>

                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        text
                        @click="cancelNotificationPayment"
                        :disabled="isProcessingNotificationPayment"
                    >
                      Cancelar
                    </v-btn>
                    <v-btn
                        color="error"
                        dark
                        @click="processNotificationPayment"
                        :loading="isProcessingNotificationPayment"
                    >
                      <v-icon left>mdi-credit-card</v-icon>
                      Confirmar Pagamento
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>

              <v-row class="mt-2">
                <v-col cols="12">
                  <div class="price-section">
                    <span class="price-label">Valor</span>
                    <span class="price-value">R$ {{ formatMoney(calculatedPrice) }}</span>
                  </div>
                </v-col>
              </v-row>

              <v-divider class="my-4"/>

              <v-row>
                <v-col cols="12">
                  <div class="payment-section">
                    <span class="payment-label">MÉTODO DE PAGAMENTO</span>
                    <div class="payment-method">
                      <strong>PIX</strong>
                    </div>
                    <div class="total-section">
                      <span class="total-label">VALOR TOTAL</span>
                      <div class="total-value">R$ {{ formatMoney(calculatedPrice) }}</div>
                    </div>
                  </div>
                </v-col>
              </v-row>

              <v-row class="mt-4">
                <v-col cols="12" md="6">
                  <v-text-field
                      v-mask="['###.###.###-##', '##.###.###/####-##']"
                      v-model="cpfCnpj"
                      label="CPF/CNPJ"
                      outlined
                      dense
                      :rules="cpfCnpjRules"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                      v-mask="['(##) ####-####', '(##) #####-####']"
                      v-model="phone"
                      label="Celular"
                      outlined
                      dense
                      :rules="phoneRules"
                  />
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12">
                  <v-text-field
                      v-model="email"
                      label="E-mail"
                      type="email"
                      outlined
                      dense
                      :rules="emailRules"
                  />
                </v-col>
              </v-row>

              <v-row class="mt-2">
                <v-col cols="12">
                  <v-checkbox
                      v-model="receiveEmailProof"
                      label="Quero receber o comprovante de ativação via e-mail"
                      dense
                      class="mt-0"
                  />

                  <v-checkbox
                      v-model="receiveSmsProof"
                      label="Quero receber o comprovante de ativação via SMS"
                      dense
                      class="mt-0"
                  />
                </v-col>
              </v-row>

              <v-row class="mt-4">
                <v-col cols="12" class="text-center">
                  <v-btn
                      color="black"
                      dark
                      large
                      block
                      :disabled="!isFormValid"
                      @click="finalizePurchase"
                      class="finalize-btn"
                  >
                    FINALIZAR COMPRA
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>

            <v-alert
                v-model="showSuccessAlert"
                type="success"
                dismissible
                class="mt-4"
            >
              <div class="success-content">
                <div class="success-message">
                  <strong>Sucesso!</strong> Seu estacionamento avulso foi ativado com sucesso.
                  <span class="d-none d-lg-inline">Confira o comprovante ao lado.</span>
                  <span class="d-lg-none">Confira o comprovante abaixo.</span>
                </div>
                <div class="success-actions mt-3">
                  <v-btn
                      color="info"
                      @click="startNewPurchase"
                      class="new-purchase-btn"
                  >
                    Nova Compra
                  </v-btn>
                  <v-btn
                      color="white"
                      light
                      outlined
                      @click="goToHome"
                      class="home-btn ml-2"
                  >
                    Página Inicial
                  </v-btn>
                </div>
              </div>
            </v-alert>

            <pix-dialog :order="order || notificationOrder"/>

            <confirmation-alert
                v-model="showConfirmationDialog"
                :messages="confirmationMessages"
                @confirm="onConfirmValidation"
                @cancel="onCancelValidation"
            />

            <public-activation-summary
                v-model="showSummaryDialog"
                :summary-data="summaryData"
                :loading="isProcessingOrder"
                @confirm="confirmPurchase"
            />
          </v-card-text>
        </v-card>
      </v-col>

      <v-col v-if="showReceipt" cols="12" lg="6" xl="5">
        <div class="receipt-side">
          <div class="receipt-container">
            <modern-activation-receipt
                :info="receiptData"
                class="receipt-card"
            />

            <div class="mobile-receipt-actions d-lg-none mt-4">
              <v-divider class="my-4"></v-divider>
              <div class="text-center">
                <v-btn
                    color="success"
                    large
                    @click="startNewPurchase"
                    class="new-purchase-btn mb-2"
                    block
                >
                  <v-icon left>mdi-plus</v-icon>
                  Nova Compra
                </v-btn>
                <v-btn
                    color="info"
                    large
                    outlined
                    @click="goToHome"
                    class="home-btn"
                    block
                >
                  <v-icon left>mdi-home</v-icon>
                  Página Inicial
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </v-col>

      <v-col cols="12" lg="12">
        <app-store></app-store>
      </v-col>
    </v-row>

    <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="snackbar.timeout"
        bottom
        right
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn
            text
            v-bind="attrs"
            @click="snackbar.show = false"
        >
          Fechar
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from 'axios';
import PixDialog from "@/components/MethodPayment/PixDialog.vue";
import ModernActivationReceipt from "@/components/Receipts/ModernActivationReceipt.vue";
import AppStore from "@/views/LandingPage/components/AppStore.vue";
import ConfirmationAlert from "@/components/Alerts/ConfirmationAlert.vue";
import PublicActivationSummary from "@/components/Activation/PublicActivationSummary.vue";

export default {
  name: "PublicActivation",
  components: {
    AppStore,
    PixDialog,
    ModernActivationReceipt,
    ConfirmationAlert,
    PublicActivationSummary
  },
  props: {},
  data() {
    return {
      order: null,
      orderStatusInterval: null,
      isFormValid: false,
      showSuccessAlert: false,
      showReceipt: false,
      receiptData: null,
      completedOrder: null,
      vehiclePlate: null,
      selectedGroup: null,
      selectedCategory: null,
      selectedProduct: null,
      cpfCnpj: null,
      phone: null,
      email: null,
      receiveEmailProof: false,
      receiveSmsProof: false,
      productGroups: [],
      notifications: [],
      isCheckingNotifications: false,
      isCheckingVehicle: false,
      vehicleDetails: null,
      showNotificationDetails: false,
      showPaymentDialog: false,
      isProcessingNotificationPayment: false,
      notificationOrder: null,
      plateInputTimeout: null,
      showNotificationPaymentSuccess: false,
      notificationSuccessTimeout: null,
      showConfirmationDialog: false,
      showSummaryDialog: false,
      isProcessingOrder: false,
      confirmationMessages: [],
      pendingValidationResolve: null,
      vehiclePlateRules: [
        v => !!v || 'Placa do veículo é obrigatória',
        v => this.validateLicensePlate(v) || 'Digite uma placa de veículo válida'
      ],
      cpfCnpjRules: [
        v => !!v || 'CPF/CNPJ é obrigatório',
        v => this.validateIdentification(v) || 'CPF/CNPJ inválido'
      ],
      phoneRules: [
        v => !!v || 'O campo celular é obrigatório',
        v => v.replace(/\D+/g, '').length === 11 || 'O celular deve conter 11 dígitos (incluindo o DDD)',
        v => /^(?:(55\d{2})|\d{2})[6-9]\d{8}$/.test(v.replace(/\D+/g, '')) || 'Digite um número válido com DDD + 9 dígitos',
      ],
      emailRules: [
        v => !!v || 'E-mail é obrigatório',
        v => this.validEmail(v) || 'E-mail deve ser válido'
      ],
      snackbar: {
        show: false,
        text: '',
        color: 'success',
        timeout: 4000
      },
    };
  },
  watch: {
    productGroups(newVal) {
      if (!newVal || newVal.length === 0) {
        return;
      }

      if (newVal.length === 1) {
        this.selectedGroup = newVal[0].id;
      }
    },
    selectedGroup(newVal) {
      this.selectedCategory = null;

      if (!newVal) {
        return;
      }

      const group = this.productGroups.find(g => g.id === newVal);

      if (!group || !group.categories) {
        return;
      }

      if (group.categories.length === 1) {
        this.$nextTick(() => {
          this.selectedCategory = group.categories[0].id;
        });
      }
    },
    selectedCategory(newVal) {
      this.selectedProduct = null;

      if (!newVal || !this.selectedGroup) {
        return;
      }

      const group = this.productGroups.find(g => g.id === this.selectedGroup);

      if (!group) {
        return;
      }

      const category = group.categories.find(c => c.id === newVal);

      if (!category || !category.products) {
        return;
      }

      if (category.products.length === 1) {
        this.$nextTick(() => {
          this.selectedProduct = category.products[0].id;
        });
      }
    }
  },
  computed: {
    appName: function () {
      return process.env.VUE_APP_NAME;
    },
    groupOptions() {
      if (!this.productGroups) {
        return []
      }

      return this.productGroups.map(g => ({
        text: g.name,
        value: g.id
      }));
    },
    categoryOptions() {
      if (!this.selectedGroup || !this.productGroups) {
        return []
      }

      const group = this.productGroups.find(g => g.id === this.selectedGroup);
      if (!group) {
        return []
      }

      return group.categories.map(c => ({
        text: c.name,
        value: c.id
      }));
    },
    filteredTimeOptions() {
      if (!this.selectedGroup || !this.selectedCategory || !this.productGroups) {
        return [];
      }

      const group = this.productGroups.find(g => g.id === this.selectedGroup);

      if (!group || !group.categories) {
        return [];
      }

      const category = group.categories.find(c => c.id === this.selectedCategory);

      if (!category) {
        return [];
      }

      return category.products.map(p => ({
        text: `${this.formatMoney(p.price)} - ${p.name}`,
        value: p.id,
        price: p.price
      }));
    },
    calculatedPrice() {
      if (!this.selectedProduct || !this.productGroups) {
        return 0;
      }

      let foundProduct = null;

      for (const group of this.productGroups) {
        for (const category of group.categories) {
          const product = category.products.find(
              p => p.id === this.selectedProduct
          );
          if (product) {
            foundProduct = product;
            break;
          }
        }
        if (foundProduct) {
          break;
        }
      }

      return foundProduct ? foundProduct.price : 0;
    },
    vehiclePlateErrors() {
      const errors = [];
      if (this.vehiclePlate && !this.validateLicensePlate(this.vehiclePlate)) {
        errors.push('Digite uma placa de veículo válida');
      }
      return errors;
    },
    hasNotifications() {
      return this.notifications && this.notifications.length > 0;
    },
    totalNotificationAmount() {
      if (!this.hasNotifications) {
        return 0;
      }

      return this.notifications.reduce((total, notification) => {
        return total + (notification.price || 0);
      }, 0);
    },
    vehicleDisplayName() {
      if (!this.vehicleDetails) {
        return '';
      }

      const makerModel = this.vehicleDetails.makerModel;
      const model = (makerModel && makerModel.name) || '';

      return model || 'Modelo Desconhecido';
    },
    vehicleColorAndType() {
      if (!this.vehicleDetails) {
        return '';
      }

      const colorObj = this.vehicleDetails.color;
      const typeObj = this.vehicleDetails.type;

      const color = (colorObj && colorObj.name) || '';
      const type = (typeObj && typeObj.name) || '';

      const parts = [color, type].filter(Boolean);
      return parts.join(' • ');
    },
    summaryData() {
      const categoryOption = this.categoryOptions.find(c => c.value === this.selectedCategory);
      const categoryName = categoryOption ? categoryOption.text : this.selectedCategory;

      return {
        plate: this.vehiclePlate,
        vehicleDescription: this.vehicleDisplayName + (this.vehicleColorAndType ? ' - ' + this.vehicleColorAndType : ''),
        category: categoryName,
        productName: this.getSelectedProductName(),
        price: this.calculatedPrice,
        document: this.cpfCnpj,
        phone: this.phone,
        email: this.email
      };
    }
  },
  mounted() {
    this.initializeForm();
  },
  beforeDestroy() {
    this.clearOrderStatusPolling();
    this.clearPlateInputTimeout();
    this.clearNotificationSuccessTimeout();
  },
  methods: {
    showSnackbar(text, color = 'success') {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    initializeForm() {
      this.loadProducts();
    },
    setDefaultValues() {
      if (this.categoryOptions.length > 0) {
        this.selectedCategory = this.categoryOptions[0].value;
      }
    },
    async loadProducts() {
      try {
        const {data: {groups}} = await axios.get(
            "/api/public/activation/products"
        );

        this.productGroups = groups;
        this.setDefaultValues();
      } catch (e) {
        console.error('Erro ao carregar produtos:', e);
      }
    },
    validateLicensePlate(plate) {
      const plateRegex = /^[A-Z]{3}[0-9][A-Z0-9][0-9]{2}$/;
      const oldPlateRegex = /^[A-Z]{3}[0-9]{4}$/;

      if (!plate) {
        return false;
      }

      const cleanPlate = plate.replace(/[^A-Z0-9]/g, '').toUpperCase();

      return plateRegex.test(cleanPlate) || oldPlateRegex.test(cleanPlate);
    },
    handlePlateInput(value) {
      if (!value) {
        this.vehiclePlate = '';
        this.clearNotifications();
        return;
      }

      const cleanPlate = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
      const formattedPlate = cleanPlate.substring(0, 7);

      this.$nextTick(() => {
        this.vehiclePlate = formattedPlate;
        this.debounceNotificationCheck();
      });
    },
    debounceNotificationCheck() {
      this.clearPlateInputTimeout();
      this.hideNotificationPaymentSuccess();

      this.plateInputTimeout = setTimeout(() => {
        if (this.vehiclePlate && this.validateLicensePlate(this.vehiclePlate)) {
          this.checkPlateNotifications();
          this.fetchVehicleDetails(this.vehiclePlate);
        }
      }, 800);
    },
    clearPlateInputTimeout() {
      if (this.plateInputTimeout) {
        clearTimeout(this.plateInputTimeout);
        this.plateInputTimeout = null;
      }
    },
    async checkPlateNotifications() {
      if (!this.vehiclePlate) {
        return;
      }

      this.isCheckingNotifications = true;

      try {
        const response = await axios.get('/api/public/notifications', {
          params: {
            plateIn: this.vehiclePlate
          }
        });

        const allNotifications = response.data.data || [];
        this.notifications = allNotifications.filter(notification => notification.status === 2);
      } catch (error) {
        console.error('Erro ao verificar notificações:', error);
        this.notifications = [];
      } finally {
        this.isCheckingNotifications = false;
      }
    },
    async fetchVehicleDetails(plate) {
      this.isCheckingVehicle = true;
      this.vehicleDetails = null;

      try {
        const {data} = await axios.get(`/api/public/vehicle/${plate}`);

        if (data && data.color && data.makerModel) {
          this.vehicleDetails = data;
          return;
        }

        const {data: dataFromImport} = await axios.get(
            `/api/public/vehicle/${plate}/premium`
        );

        this.vehicleDetails = dataFromImport;

      } catch (error) {
        console.error('Erro ao buscar detalhes do veículo:', error);
      } finally {
        this.isCheckingVehicle = false;
      }
    },
    clearNotifications() {
      this.notifications = [];
      this.vehicleDetails = null;
      this.showNotificationDetails = false;
      this.hideNotificationPaymentSuccess();
    },
    toggleNotificationDetails() {
      this.showNotificationDetails = !this.showNotificationDetails;
    },
    getNotificationTypeName(notification) {
      if (notification.type_enum) {
        return notification.type_enum;
      }

      if (notification.parking_notification_type) {
        return notification.parking_notification_type.name;
      }

      return 'Notificação';
    },
    getVehicleDescription(vehicle) {
      if (!vehicle) {
        return '';
      }

      const parts = [];

      if (vehicle.makerModel && vehicle.makerModel.name) {
        parts.push(vehicle.makerModel.name);
      }

      if (vehicle.color && vehicle.color.name) {
        parts.push(vehicle.color.name);
      }

      return parts.length > 0 ? parts.join(' - ') : 'Veículo';
    },
    showNotificationPayment() {
      if (!this.hasNotifications) {
        return;
      }

      this.showPaymentDialog = true;
    },
    cancelNotificationPayment() {
      this.showPaymentDialog = false;
      this.isProcessingNotificationPayment = false;
    },
    async processNotificationPayment() {
      if (!this.hasNotifications) {
        return;
      }

      this.isProcessingNotificationPayment = true;

      try {
        const createdOrder = await this.createNotificationOrder();
        this.notificationOrder = createdOrder;
        this.showPaymentDialog = false;

        this.startOrderStatusPolling(createdOrder.id, 'notification');
      } catch (error) {
        console.error('Erro ao processar pagamento das notificações:', error);
        alert('Erro ao processar pagamento. Tente novamente.');
      } finally {
        this.isProcessingNotificationPayment = false;
      }
    },
    async createNotificationOrder() {
      try {
        const response = await axios.post('/api/public/order/notification/pay', {
          transaction_type: 17,
          hold: false,
          is_from_public_request: true,
          confirmation_type: 3,
          payment_form_input: [
            {
              payment_form_id: 11,
            },
          ],
          notifications: this.notifications.map(
              (notification) => notification.id
          ),
        });

        return response.data;
      } catch (error) {
        console.error('Erro ao criar pedido de notificação:', error);
        throw error;
      }
    },
    showNotificationPaymentSuccessMessage() {
      this.showNotificationPaymentSuccess = true;

      this.clearNotificationSuccessTimeout();
      this.notificationSuccessTimeout = setTimeout(() => {
        this.hideNotificationPaymentSuccess();
      }, 5000);
    },
    hideNotificationPaymentSuccess() {
      this.showNotificationPaymentSuccess = false;
      this.clearNotificationSuccessTimeout();
    },
    clearNotificationSuccessTimeout() {
      if (this.notificationSuccessTimeout) {
        clearTimeout(this.notificationSuccessTimeout);
        this.notificationSuccessTimeout = null;
      }
    },
    formatNotificationDate(dateString) {
      if (!dateString) {
        return '';
      }

      const date = new Date(dateString);

      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    async finalizePurchase() {
      const isFormValid = this.$refs.form.validate();

      if (!isFormValid) {
        return;
      }

      try {
        await this.checkParkingValidation();
        this.showSummaryDialog = true;
      } catch (e) {
        if (e.message !== 'Cancelled') {
          console.error('Erro ao finalizar compra:', e);
        }
      }
    },
    async confirmPurchase() {
      this.isProcessingOrder = true;
      try {
        const createdOrder = await this.createOrder();
        this.startOrderStatusPolling(createdOrder.id);
        this.showSummaryDialog = false;
      } catch (e) {
        console.error('Erro ao confirmar compra:', e);
      } finally {
        this.isProcessingOrder = false;
      }
    },
    async checkParkingValidation() {
      try {
        const {data} = await axios.get('/api/public/activation/validate', {
          params: {
            plate: this.vehiclePlate,
            product_id: this.selectedProduct
          }
        });

        if (data.messages && data.messages.length > 0) {
          this.confirmationMessages = data.messages;
          this.showConfirmationDialog = true;

          const confirmed = await new Promise((resolve) => {
            this.pendingValidationResolve = resolve;
          });

          if (!confirmed) {
            throw new Error('Cancelled');
          }
        }
      } catch (e) {
        if (e.message === 'Cancelled') {
          throw e;
        }

        const errorMessage = e.message || "Erro ao validar estacionamento";

        this.showSnackbar(errorMessage, "error");
        throw e;
      }
    },
    onConfirmValidation() {
      this.showConfirmationDialog = false;
      if (this.pendingValidationResolve) {
        this.pendingValidationResolve(true);
        this.pendingValidationResolve = null;
      }
    },
    onCancelValidation() {
      this.showConfirmationDialog = false;
      if (this.pendingValidationResolve) {
        this.pendingValidationResolve(false);
        this.pendingValidationResolve = null;
      }
    },
    async createOrder() {
      try {
        const response = await axios.post('/api/public/order', {
          plate: this.vehiclePlate,
          product_id: this.selectedProduct,
          identification: this.cpfCnpj,
          quantity: 1,
          transaction_type: 6,
          confirmation_type: 7,
          hold: false,
          payment_form_input: [
            {
              payment_form_id: 11
            },
          ],
          receipt: {
            email: this.receiveEmailProof ? this.email : null,
            phone: this.receiveSmsProof ? this.phone : null,
          },
          is_from_public_request: true
        });

        this.order = response.data;
        return response.data;
      } catch (e) {
        const errorMessage =
            (e.response && e.response.data && e.response.data.message) ||
            (e.response && e.response.data && e.response.data.error) ||
            e.message ||
            "Erro ao criar pedido";

        this.showSnackbar(errorMessage, "error");

        throw e;
      }
    },
    startOrderStatusPolling(orderId, orderType = 'regular') {
      this.clearOrderStatusPolling();

      this.orderStatusInterval = setInterval(() => {
        this.checkOrderStatus(orderId, orderType);
      }, 10000);
    },
    async checkOrderStatus(orderId, orderType = 'regular') {
      try {
        const response = await axios.get(`/api/public/order/${orderId}`);
        const orderData = response.data;
        const orderStatus = orderData.status;

        if (orderStatus === 2) {
          this.completedOrder = orderData;

          if (orderType === 'notification') {
            this.handleNotificationOrderCompleted();
          } else {
            this.handleOrderCompleted();
          }
        }
      } catch (error) {
        console.error('Erro ao verificar status do pedido:', error);
      }
    },
    handleNotificationOrderCompleted() {
      this.clearOrderStatusPolling();
      this.notificationOrder = null;

      this.showNotificationPaymentSuccessMessage();
      this.clearNotifications();

      this.$nextTick(() => {
        if (this.vehiclePlate && this.validateLicensePlate(this.vehiclePlate)) {
          this.checkPlateNotifications();
        }
      });
    },
    handleOrderCompleted() {
      this.clearOrderStatusPolling();
      this.order = null;
      this.showSuccessAlert = true;
      this.prepareReceiptData();

      this.showReceipt = true;

      this.clearFormInputs();

      this.$nextTick(() => {
        if (window.innerWidth < 1264) {
          const receiptElement = document.querySelector('.receipt-container');
          if (receiptElement) {
            receiptElement.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      });
    },
    clearFormInputs() {
      this.vehiclePlate = null;
      this.selectedCategory = null;
      this.selectedProduct = null;
      this.cpfCnpj = null;
      this.phone = null;
      this.email = null;
      this.receiveEmailProof = false;
      this.receiveSmsProof = false;
      this.isFormValid = false;

      this.clearNotifications();

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }

      this.setDefaultValues();
    },
    prepareReceiptData() {
      if (!this.completedOrder) {
        return;
      }

      const orderData = this.completedOrder;
      const currentTimestamp = Math.floor(Date.now() / 1000);

      this.receiptData = {
        title: 'Comprovante de Ativação',
        transaction_id: orderData.id || null,
        product: this.getSelectedProductName(),
        date: currentTimestamp,
        targetName: null,
        plate: this.vehiclePlate,
        maker_model: null,
        date_time_begin: currentTimestamp,
        date_time_end: this.calculateEndTime(currentTimestamp),
        time: this.getSelectedProductTime(),
        price: orderData.total_amount || (this.calculatedPrice * 100),
        tax: null,
        discount: null,
        total_amount: orderData.total_amount || (this.calculatedPrice * 100),
        method: 'PIX'
      };
    },
    getSelectedProductName() {
      if (!this.selectedProduct || !this.productGroups) {
        return null;
      }

      for (const group of this.productGroups) {
        for (const category of group.categories) {
          const product = category.products.find(
              p => p.id === this.selectedProduct
          );
          if (product) {
            return product.name;
          }
        }
      }

      return null;
    },
    getSelectedProductTime() {
      const productName = this.getSelectedProductName();

      if (!productName) {
        return null;
      }

      const timeMatch = productName.match(/(\d+)\s*min/i);

      return timeMatch ? parseInt(timeMatch[1]) : null;
    },
    calculateEndTime(startTime) {
      const timeInMinutes = this.getSelectedProductTime();

      if (!timeInMinutes) {
        return startTime + (60 * 60);
      }

      return startTime + (timeInMinutes * 60);
    },
    startNewPurchase() {
      this.showSuccessAlert = false;
      this.showReceipt = false;
      this.clearFormData();
    },
    goToHome() {
      window.location.href = '/';
    },
    clearFormData() {
      this.clearFormInputs();

      this.clearNotifications();
      this.cancelNotificationPayment();

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    clearOrderStatusPolling() {
      if (this.orderStatusInterval) {
        clearInterval(this.orderStatusInterval);
        this.orderStatusInterval = null;
      }
    },
    validEmail(value) {
      const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

      return pattern.test(value);
    },
    validateIdentification(value) {
      if (!value) {
        return false;
      }

      const cleanNumber = value.replace(/\D/g, '');

      if (cleanNumber.length === 11) {
        return this.validateCPF(cleanNumber);
      }

      if (cleanNumber.length === 14) {
        return this.validateCNPJ(cleanNumber);
      }

      return false;
    },
    validateCPF(cpf) {
      if (/^(\d)\1{10}$/.test(cpf)) {
        return false;
      }

      let sum = 0;
      let remainder;

      for (let i = 1; i <= 9; i++) {
        sum += parseInt(cpf.substring(i - 1, i)) * (11 - i);
      }

      remainder = (sum * 10) % 11;
      if (remainder === 10 || remainder === 11) {
        remainder = 0;
      }
      if (remainder !== parseInt(cpf.substring(9, 10))) {
        return false;
      }

      sum = 0;
      for (let i = 1; i <= 10; i++) {
        sum += parseInt(cpf.substring(i - 1, i)) * (12 - i);
      }

      remainder = (sum * 10) % 11;
      if (remainder === 10 || remainder === 11) {
        remainder = 0;
      }
      if (remainder !== parseInt(cpf.substring(10, 11))) {
        return false;
      }

      return true;
    },
    validateCNPJ(cnpj) {
      if (/^(\d)\1{13}$/.test(cnpj)) {
        return false;
      }

      const weights = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

      let sum = 0;
      for (let i = 0; i < 12; i++) {
        sum += parseInt(cnpj.charAt(i)) * weights[i + 1];
      }

      let remainder = sum % 11;
      const firstDigit = remainder < 2 ? 0 : 11 - remainder;
      if (parseInt(cnpj.charAt(12)) !== firstDigit) {
        return false;
      }

      sum = 0;
      for (let i = 0; i < 13; i++) {
        sum += parseInt(cnpj.charAt(i)) * weights[i];
      }

      remainder = sum % 11;
      const secondDigit = remainder < 2 ? 0 : 11 - remainder;
      if (parseInt(cnpj.charAt(13)) !== secondDigit) {
        return false;
      }

      return true;
    }
  }
}
</script>

<style scoped>
.public-activation-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
}

.parking-form {
  height: fit-content;
  position: sticky;
  top: 20px;
}

.form-title {
  font-size: 24px;
  font-weight: 400;
  color: #333;
}

.price-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}

.price-label {
  color: #666;
  font-size: 14px;
}

.price-value {
  color: #2196F3;
  font-weight: 500;
  font-size: 16px;
}

.payment-section {
  background-color: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
}

.payment-label {
  color: #888;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 1px;
}

.payment-method {
  font-size: 20px;
  font-weight: bold;
  margin: 8px 0;
}

.total-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #ddd;
}

.total-label {
  color: #888;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 1px;
  display: block;
}

.total-value {
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin-top: 4px;
}

.finalize-btn {
  font-weight: bold;
  letter-spacing: 1px;
  padding: 16px 0 !important;
  height: auto !important;
}

.v-input--checkbox {
  margin-bottom: 8px;
}

.v-input--checkbox .v-label {
  font-size: 14px;
  color: #666;
}

.success-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.success-message {
  font-size: 16px;
}

.success-actions {
  align-self: center;
}

.new-purchase-btn,
.home-btn {
  font-weight: bold;
  letter-spacing: 0.5px;
}

.notification-alert {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.notification-alert-content {
  display: flex;
  align-items: center;
  flex: 1;
}

.notification-alert-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.notification-title {
  font-weight: 500;
  color: #e65100;
}

.notification-success-alert {
  display: flex;
  align-items: center;
  width: 100%;
}

.notification-success-title {
  font-weight: 500;
  color: #2e7d32;
}

.notification-details {
  margin-top: 8px;
}

.notification-details-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.notification-item {
  border-left: 3px solid #ff9800;
  padding-left: 12px;
}

.notification-card {
  background: #fafafa;
  border-radius: 8px;
  padding: 12px;
  border: 1px solid #e0e0e0;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.notification-type-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.notification-type {
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.notification-status {
  font-size: 12px;
  color: #666;
  background: #e0e0e0;
  padding: 2px 8px;
  border-radius: 12px;
  align-self: flex-start;
}

.notification-amount {
  color: #d32f2f;
  font-weight: 600;
  font-size: 16px;
}

.notification-details-content {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.notification-info-row {
  display: flex;
  align-items: center;
  color: #666;
  font-size: 13px;
}

.notification-date,
.notification-location,
.notification-vehicle {
  margin-left: 4px;
}

.notification-total-section {
  background: #f5f5f5;
  padding: 16px;
  border-radius: 8px;
  margin-top: 8px;
}

.notification-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.total-label {
  font-size: 14px;
  color: #666;
}

.total-amount {
  font-size: 18px;
  font-weight: 600;
  color: #d32f2f;
}

.payment-dialog-content {
  max-height: 400px;
  overflow-y: auto;
}

.payment-summary {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  font-size: 14px;
}

.summary-amount {
  font-weight: 500;
  color: #d32f2f;
}

.summary-total {
  text-align: center;
  font-size: 16px;
  color: #d32f2f;
  margin-top: 8px;
}

.payment-method-info {
  display: flex;
  align-items: center;
  background: #e3f2fd;
  padding: 12px;
  border-radius: 4px;
  color: #1976d2;
}

.receipt-side {
  height: 100%;
}

.receipt-container {
  position: sticky;
  top: 20px;
}

.receipt-header-title {
  text-align: center;
}

.receipt-card {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15) !important;
  border-radius: 12px !important;
}

.mobile-receipt-actions {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  margin-top: 20px;
}

.vehicle-info-card {
  border-color: #e0e0e0;
  background-color: #f8fbff;
}

@media (max-width: 1263px) {
  .public-activation-container {
    padding: 10px;
  }

  .parking-form {
    position: relative;
    top: auto;
    margin-bottom: 20px;
  }

  .receipt-container {
    position: relative;
    top: auto;
  }

  .success-actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }

  .notification-alert {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .notification-alert-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .notification-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .notification-amount {
    align-self: flex-end;
  }
}

@media (max-width: 600px) {
  .success-actions .v-btn {
    margin: 4px 0;
  }

  .notification-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }

  .notification-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .notification-alert-actions {
    flex-direction: column;
    width: 100%;
    gap: 8px;
  }

  .notification-alert-actions .v-btn {
    width: 100%;
  }
}

.receipt-container {
  animation: slideInFromRight 0.5s ease-out;
}

@keyframes slideInFromRight {
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>
